/**
 * locationClassification.ts — the block + block-group classification of a searched site (T-512).
 *
 * Noreen (2026-07-25) asked to read a location's classification and score in the STATS panel, not
 * as map tiles: "block is corridor, corridor exposure score X" and "in a block group single-family
 * classification, score Y". The data already ships to the client via GET /api/vicinity (T-504) —
 * every nearby block and block group with its server-computed classification and scores. This is
 * the seam that turns that collection into the one block and one block group the site actually sits
 * in, ready for an honest readout.
 *
 * WHAT IS AUTHORITATIVE AND WHAT IS NOT.
 *   - The CLASSIFICATIONS are the model's, read verbatim from the served features — this code
 *     never invents or re-derives a class or a score. `block_dominant_type` is NOT NULL in
 *     census_blocks (migration 017: only Corridor / Retail/Leisure blocks exist), so a location
 *     with no covering block feature is the model's "block-level data dropped" case (Noreen's
 *     scheme) — it inherits the block group's residential classification, surfaced here as
 *     `no_block_coverage` so the note can say exactly that.
 *   - Score→class mapping (verified against migration 017 / 014 + scoreDefinitions):
 *       Corridor        → ces_blk  "corridor exposure score"  (0-100 scale)
 *       Retail/Leisure  → rds_blk  "retail destination score" ([0,1])
 *       Residential_MFH → hcc_bg   (share who can't easily charge at home; multifamily)  ([0,1])
 *       Suburban_SFH    → hco_bg   (share who can charge at home; single-family)         ([0,1])
 *
 * RESOLVED (T-583, Noreen 2026-07-29) — the open item this file used to carry was whether a block
 * group's residential type is the higher of its single-family vs multi-family prevalence. It is:
 * the producer now ends in a plurality branch over {HCC_bg, HCO_bg} after the two thresholds. That
 * derivation still lives UPSTREAM and this UI still only reads the stamp — which is exactly why the
 * confirmation changed the producer and not a line of arithmetic here. What it DID change is what an
 * unlabelled row means, and that is a wording question this file owns: see BgStatus below (T-584).
 */
import type * as GeoJSON from 'geojson'
import { pointInPolygon } from './pointInPolygon'
import { DOMINANT_TYPE_LABELS, isNonResidentialBasis, NON_RESIDENTIAL_LABEL } from './tractModel'

export type BlockRawType = 'Corridor' | 'Retail/Leisure' | string
export type BgRawType = 'Residential_MFH' | 'Suburban_SFH' | string

/**
 * T-628: every block score the vicinity payload carries, verbatim. Until this card the readout
 * kept only the one its class is read on and dropped the other three, even though all four arrive
 * in the same response. The site-specific section shows them, so they are modelled — and absence
 * stays null, because a block with no dwell-time measurement is not a block with dwell time 0.
 */
export interface BlockScores {
  /** ces_blk — corridor exposure, 0-100. */
  corridorExposure: number | null
  /** cs_blk — corridor score, [0,1]. */
  corridor: number | null
  /** rds_blk — retail destination score, [0,1]. */
  retailDestination: number | null
  /** dts_blk — daytime/dwell score, [0,1]. */
  dwellTime: number | null
}

/** T-628: both block-group scores, not only the one the class is read on. Always [0,1] or null. */
export interface BgScores {
  /** hcc_bg — share who cannot easily charge at home (multifamily signal). */
  homeChargingConstrained: number | null
  /** hco_bg — share who can charge at home (single-family signal). */
  homeChargingOptimized: number | null
}

export interface BlockClassification {
  /** 15-digit census block geoid, when the feature carried one. */
  geoid: string | null
  rawType: BlockRawType
  /** Customer-facing word for the class. */
  label: string
  /** Which score the class is read on — Noreen's exact phrasing. */
  scoreLabel: 'corridor exposure score' | 'retail destination score'
  /** The score value, verbatim from the model; null if the feature omitted it (never invented). */
  score: number | null
  /** `centi` = 0-100 (ces), `unit` = [0,1] (rds). Formatting precision is the note's concern. */
  scoreScale: 'centi' | 'unit'
  /** T-628: every block score served for this block, including the three its class is not read on. */
  scores: BlockScores
}

export interface BgClassification {
  /** 12-digit census block-group geoid, when the feature carried one. */
  geoid: string | null
  rawType: BgRawType
  label: string
  /** hcc_bg (MFH) or hco_bg (SFH), verbatim; null if omitted. Always [0,1]. */
  score: number | null
  /** T-628: both scores, so the site-specific section can show the one the class is NOT read on. */
  scores: BgScores
}

export type BlockStatus = 'classified' | 'no_block_coverage'
/**
 * T-584 — `unclassified_bg` and `unscored_bg` are two different facts that used to share a
 * sentence, and after T-583 they swap populations entirely:
 *
 *   unclassified_bg  a covering block group WAS scored and met neither residential rule
 *   unscored_bg      a covering block group carries no ACS scores at all — nothing to classify
 *
 * Before T-583 the first was the common case and the second was invisible inside it. T-583's
 * plurality fallback labels every block group with any score, so once Noreen's re-run publishes
 * the first becomes empty and the second is the whole of it. One sentence covering both was
 * therefore about to become a false statement rather than a merely imprecise one.
 */
export type BgStatus = 'classified' | 'unclassified_bg' | 'unscored_bg' | 'no_bg_coverage'

export interface LocationClassification {
  block: BlockClassification | null
  blockStatus: BlockStatus
  blockGroup: BgClassification | null
  bgStatus: BgStatus
}

/** Coerce a served property to a finite number, or null — the ::float8 casts should already make
 *  these numbers, but a missing/garbled value must degrade to "no number", never to a fake 0. */
function asFiniteNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

/** The first feature whose geometry contains the site point (features are non-overlapping). */
function containingFeature(
  site: { lat: number; lng: number },
  fc: GeoJSON.FeatureCollection | null | undefined,
): GeoJSON.Feature | null {
  if (!fc?.features) return null
  for (const f of fc.features) {
    if (f.geometry && pointInPolygon(site.lng, site.lat, f.geometry)) return f
  }
  return null
}

function classifyBlock(feature: GeoJSON.Feature | null): {
  block: BlockClassification | null
  blockStatus: BlockStatus
} {
  const p = (feature?.properties ?? null) as Record<string, unknown> | null
  const rawType = p?.['block_dominant_type']
  // T-628: read verbatim, once, for whichever class matches below.
  const scores: BlockScores = {
    corridorExposure: asFiniteNumber(p?.['ces_blk']),
    corridor: asFiniteNumber(p?.['cs_blk']),
    retailDestination: asFiniteNumber(p?.['rds_blk']),
    dwellTime: asFiniteNumber(p?.['dts_blk']),
  }
  if (rawType === 'Corridor') {
    return {
      blockStatus: 'classified',
      block: {
        geoid: asString(p?.['block_geoid']),
        rawType: 'Corridor',
        label: 'Corridor',
        scoreLabel: 'corridor exposure score',
        score: scores.corridorExposure,
        scoreScale: 'centi',
        scores,
      },
    }
  }
  if (rawType === 'Retail/Leisure') {
    return {
      blockStatus: 'classified',
      block: {
        geoid: asString(p?.['block_geoid']),
        rawType: 'Retail/Leisure',
        label: 'Retail',
        scoreLabel: 'retail destination score',
        score: scores.retailDestination,
        scoreScale: 'unit',
        scores,
      },
    }
  }
  // Block is not classified as Corridor or Retail, but still carry its scores
  // so the Location Scores section can show them regardless of classification.
  if (feature) {
    return {
      blockStatus: 'no_block_coverage',
      block: {
        geoid: asString(p?.['block_geoid']),
        rawType: typeof rawType === 'string' ? rawType : 'Unclassified',
        label: typeof rawType === 'string' ? rawType : 'Unclassified',
        scoreLabel: 'corridor exposure score',
        score: scores.corridorExposure,
        scoreScale: 'centi',
        scores,
      },
    }
  }
  return { block: null, blockStatus: 'no_block_coverage' }
}

function classifyBlockGroup(feature: GeoJSON.Feature | null): {
  blockGroup: BgClassification | null
  bgStatus: BgStatus
} {
  if (!feature) return { blockGroup: null, bgStatus: 'no_bg_coverage' }
  const p = (feature.properties ?? {}) as Record<string, unknown>
  const rawType = p['bg_dominant_type']
  const bgScores: BgScores = {
    homeChargingConstrained: asFiniteNumber(p['hcc_bg']),
    homeChargingOptimized: asFiniteNumber(p['hco_bg']),
  }
  if (rawType === 'Residential_MFH' || rawType === 'Suburban_SFH') {
    return {
      bgStatus: 'classified',
      blockGroup: {
        geoid: asString(p['bg_geoid']),
        rawType,
        label: DOMINANT_TYPE_LABELS[rawType] ?? rawType,
        score:
          rawType === 'Residential_MFH'
            ? bgScores.homeChargingConstrained
            : bgScores.homeChargingOptimized,
        scores: bgScores,
      },
    }
  }
  // A block group covers the point but carries no residential class. WHY it has none is the
  // question, and the payload answers it: the producer (`classify_bg_dominant_type`, T-583) labels
  // every block group it has ANY score for, so an unlabelled row with a score is a real
  // measurement and an unlabelled row with neither score is missing data.
  //
  // The predicate mirrors the producer's own `plurality.notna().any(axis=1)` — a HALF-scored row
  // "falls to the score it does have" and gets a label, so only a row missing both is genuinely
  // unscored. Reading it as `!hcc || !hco` instead would report missing data for a block group we
  // actually measured. This code still never derives a class; it only reports which absence it is.
  const scored =
    bgScores.homeChargingConstrained !== null || bgScores.homeChargingOptimized !== null
  if (scored) {
    // Return scores even when block group isn't classified as residential
    return {
      bgStatus: 'unclassified_bg',
      blockGroup: {
        geoid: asString(p['bg_geoid']),
        rawType: typeof rawType === 'string' ? rawType : 'Unclassified',
        label:
          typeof rawType === 'string' ? (DOMINANT_TYPE_LABELS[rawType] ?? rawType) : 'Unclassified',
        score: bgScores.homeChargingConstrained ?? bgScores.homeChargingOptimized,
        scores: bgScores,
      },
    }
  }
  return { blockGroup: null, bgStatus: 'unscored_bg' }
}

/**
 * The block and block-group classification for a searched site, from the vicinity payload it
 * already fetched. Every value is the model's; absence is reported as a status, never as a zero.
 */
export function selectLocationClassification(
  site: { lat: number; lng: number },
  vicinity: {
    blocks: GeoJSON.FeatureCollection | null | undefined
    blockGroups: GeoJSON.FeatureCollection | null | undefined
  },
): LocationClassification {
  const block = classifyBlock(containingFeature(site, vicinity.blocks))
  const blockGroup = classifyBlockGroup(containingFeature(site, vicinity.blockGroups))
  return { ...block, ...blockGroup }
}

// ── T-498: shared honest-wording helpers ─────────────────────────────────────────────────────────
//
// The stats-panel readout (LocationClassificationNote) and the PDF one-pager render the SAME
// classification, so the customer-facing wording — which class is the headline, and the two honest
// supporting sentences — lives here once. Both surfaces re-project this pure output into their own
// presentation. Nothing below invents a class or a number: it only re-labels selectLocationClassification.

/** Corridor exposure is 0-100 (1 decimal); every other score is a [0,1] share (2 decimals). A
 *  missing score reads "unavailable" — never a fabricated 0. */
export function formatClassScore(score: number | null, scale: 'centi' | 'unit'): string {
  if (score === null) return 'unavailable'
  return scale === 'centi' ? score.toFixed(1) : score.toFixed(2)
}

export interface ClassificationPrimary {
  /** Customer-facing class word — the headline location fact (e.g. "Corridor"). */
  label: string
  /** How the accompanying score is named — Noreen's phrasing for a block, or plain "score" for a
   *  block-group residential share (which has no defensible good/bad banding, so it is stated, not
   *  colour-graded).
   *
   *  NULL for a class that has no score to name (T-616's Non-Residential). That is different from a
   *  score that is MISSING: a missing score reads "unavailable" because a number was expected and
   *  did not arrive, whereas Non-Residential is named BY the absence of residential input, so
   *  printing "unavailable" beside it would imply a measurement that ought to exist. */
  scoreLabel: string | null
  /** The formatted score, "unavailable" when the class is known but its score was omitted, or null
   *  when the class carries no score at all (see `scoreLabel`). */
  scoreText: string | null
  /** 'block' when the site sits in a classified corridor/retail block; 'blockGroup' when the block
   *  data dropped and the residential block-group class is the headline it inherits instead. */
  source: 'block' | 'blockGroup'
}

/**
 * The ONE classification to feature prominently: the containing block's class when it is classified
 * (corridor/retail — the most specific location fact), otherwise the residential class of the block
 * group it inherits from. Null when NEITHER is classified — the honest "nothing to feature" case, so
 * a prominent surface falls back to a plain absence note rather than an empty badge.
 */
export function classificationPrimary(c: LocationClassification): ClassificationPrimary | null {
  if (c.blockStatus === 'classified' && c.block) {
    return {
      label: c.block.label,
      scoreLabel: c.block.scoreLabel,
      scoreText: formatClassScore(c.block.score, c.block.scoreScale),
      source: 'block',
    }
  }
  if (c.bgStatus === 'classified' && c.blockGroup) {
    return {
      label: c.blockGroup.label,
      scoreLabel: 'score',
      scoreText: formatClassScore(c.blockGroup.score, 'unit'),
      source: 'blockGroup',
    }
  }
  // T-616 — the no-residential-input block group is a NAMED area, so it earns the headline too.
  //
  // This case used to return null: prominence was withheld because the state had no name, and an
  // unnamed absence in a badge would have read as a fabricated class. Noreen has now named it, and
  // withholding it became the inconsistency — a TRACT with the same reality already shows
  // "Non-Residential" from its stored dominant_type, so the same reader would get a name from one
  // geography and silence from the other.
  //
  // Naming it is not inventing a measurement: the badge carries NO score, which is the honest shape
  // for a class defined by the absence of one. The boundary is the shared predicate's, not a local
  // reading of the scores — a block group we DID measure keeps returning null below.
  if (isNonResidentialBasis(c.bgStatus)) {
    return {
      label: NON_RESIDENTIAL_LABEL,
      scoreLabel: null,
      scoreText: null,
      source: 'blockGroup',
    }
  }
  return null
}

export interface ClassificationSentences {
  /** The block-level sentence: its class + score, or why there is no block-level class. */
  block: string
  /** The block-group sentence: its residential class + score, scored-but-neither, no residential
   *  data for a covering block group, or no covering block group at all. */
  blockGroup: string
}

function blockSentence(block: BlockClassification | null, blockStatus: BlockStatus): string {
  if (blockStatus === 'classified' && block) {
    const value = formatClassScore(block.score, block.scoreScale)
    return `This block is classified ${block.label} — ${block.scoreLabel} ${value}.`
  }
  return 'No classified corridor or retail block covers this location, so its classification comes from the block group.'
}

/**
 * T-547 — when the block carries its own class, LEAD with precedence.
 *
 * Noreen (2026-07-28): the panel was leading with the residential scoring fact even where that class
 * plays no part in the outcome, which read as though the residential number were the finding. The
 * approved wording states the reason first — "Residential classification is not applied here — the
 * block's Corridor classification takes precedence" — and keeps the underlying fact after it. She
 * confirmed the Retail variant must read with the same structure, so the class word is interpolated
 * from `block.label` rather than written twice: the two variants cannot drift apart.
 *
 * `precedingBlock` is non-null ONLY when a block is actually classified, which is what makes the
 * precedence claim true. See the no-coverage branch for the case it deliberately excludes.
 */
function blockGroupSentence(
  blockGroup: BgClassification | null,
  bgStatus: BgStatus,
  precedingBlock: BlockClassification | null,
): string {
  if (precedingBlock) {
    const lead = `Residential classification is not applied here — the block's ${precedingBlock.label} classification takes precedence.`
    if (bgStatus === 'classified' && blockGroup) {
      const value = formatClassScore(blockGroup.score, 'unit')
      return `${lead} Its block group is classified ${blockGroup.label}, score ${value} — shown for reference.`
    }
    if (bgStatus === 'unclassified_bg') {
      // The trailing "so no residential classification applies" is dropped here and only here: the
      // lead already said that, and repeating it makes the sentence give two different reasons for
      // one absence. The measurement — scored, neither-dominant — is the part that adds anything.
      return `${lead} Its block group was scored and is neither multi-family- nor single-family-dominant.`
    }
    // unscored_bg / no_bg_coverage: nothing was classified, so nothing was outranked. Claiming
    // precedence here would assert a false CAUSE — the residential class is missing because we have
    // no data for this point, not because the block beat it. A data gap must keep reading as a data
    // gap. T-584 puts the scoreless block group on this side of that line, where it belongs: the
    // block did not outrank a residential class, there was never one to outrank.
    if (bgStatus === 'unscored_bg') return `Its block group is ${NON_RESIDENTIAL_LABEL}.`
    return 'No block-group data covers this location yet.'
  }
  if (bgStatus === 'classified' && blockGroup) {
    const value = formatClassScore(blockGroup.score, 'unit')
    return `It sits in a block group classified ${blockGroup.label}, score ${value}.`
  }
  if (bgStatus === 'unclassified_bg') {
    return 'Its block group was scored and is neither multi-family- nor single-family-dominant, so no residential classification applies.'
  }
  if (bgStatus === 'unscored_bg') {
    // T-616 gives this area Noreen's name. Still deliberately says nothing about what the block
    // group is LIKE: "neither multi-family- nor single-family-dominant" would be a finding we never
    // made, so the clause after the dash reports the absence of measurement, not a measurement of
    // absence. The name and the evidence for it, in that order.
    return `It sits in a ${NON_RESIDENTIAL_LABEL} block group — no residential scores are measured there.`
  }
  return 'No block-group data covers this location yet.'
}

/** The two honest supporting sentences for a location's classification, shared by both surfaces. */
export function classificationSentences(c: LocationClassification): ClassificationSentences {
  return {
    block: blockSentence(c.block, c.blockStatus),
    // Only a CLASSIFIED block takes precedence — `blockStatus` is the guard, so a dropped-block
    // location can never claim one.
    blockGroup: blockGroupSentence(
      c.blockGroup,
      c.bgStatus,
      c.blockStatus === 'classified' ? c.block : null,
    ),
  }
}

// ── T-628: the site-specific readout ─────────────────────────────────────────────────────────────
//
// Noreen: surface the block-level scores where they exist, above the tract figures, because the
// most local number is the one a site decision turns on. Everything below is pure re-projection of
// selectLocationClassification — no score is derived, and a score the model does not have is
// OMITTED rather than printed as "unavailable" or 0. Silence is the honest empty state here: a
// site with no classified block simply has no site-specific row to show.

/** One row of the site-specific readout: a real measured score, and the geography it describes. */
export interface SiteScoreRow {
  key: string
  label: string
  value: number
  /** Formatting scale — `centi` is 0-100 (corridor exposure), `unit` is a [0,1] share. */
  scale: 'centi' | 'unit'
  level: 'block' | 'block-group'
}

/**
 * Every block / block-group score this site actually has, finest geography first.
 *
 * Block rows only appear when the block is classified (Corridor or Retail/Leisure) — those are
 * the only blocks that pass the scoring threshold and exist in the database. Block group rows
 * (Home-Charging) always appear when the block group is classified. An empty array means
 * "say nothing" — the caller renders no section at all.
 */
export function siteSpecificRows(c: LocationClassification | null): SiteScoreRow[] {
  const rows: SiteScoreRow[] = []
  const b = c?.block
  if (b) {
    // All block scores are 0-1 scale — multiply by 100 for display
    const block: Array<[string, string, number | null]> = [
      ['corridor', 'Corridor Score', b.scores.corridor],
      ['retailDestination', 'Retail Destination Score', b.scores.retailDestination],
      ['dwellTime', 'Dwell Time Score', b.scores.dwellTime],
    ]
    for (const [key, label, value] of block) {
      if (value !== null)
        rows.push({ key, label, value: Math.round(value * 100), scale: 'centi', level: 'block' })
    }
  }
  const g = c?.blockGroup
  if (g) {
    // Block group scores are 0-1 scale — multiply by 100 for display
    const bg: Array<[string, string, number | null]> = [
      ['homeChargingConstrained', 'Home-Charging Constrained', g.scores.homeChargingConstrained],
      ['homeChargingOptimized', 'Home-Charging Optimized', g.scores.homeChargingOptimized],
    ]
    for (const [key, label, value] of bg) {
      if (value !== null)
        rows.push({
          key,
          label,
          value: Math.round(value * 100),
          scale: 'centi',
          level: 'block-group',
        })
    }
  }
  return rows
}

/**
 * The block and the tract stamp the same idea with different words — blocks carry
 * 'Retail/Leisure' where tracts carry 'Retail'. Comparing the raw stamps would report a mismatch
 * that does not exist, so the two vocabularies are reconciled in ONE place, here, rather than by
 * every caller remembering the exception.
 */
function toTractVocabulary(blockRawType: BlockRawType): string {
  return blockRawType === 'Retail/Leisure' ? 'Retail' : blockRawType
}

/**
 * Is the site's own block a DIFFERENT kind of place than its tract?
 *
 * This is the compact card's materiality test (Ricardo: block data lifts onto the card when it is
 * available and crucial). A class mismatch is the case that changes a decision — a corridor site
 * inside a residential tract reads as residential on a tract-level card, which is precisely the
 * confusion this card exists to remove. Matching classes add no information to the headline, so
 * the card stays quiet and the full readout still sits in the panel.
 */
export function blockDiffersFromTract(
  c: LocationClassification | null,
  tractDominantType: string | null | undefined,
): boolean {
  const b = c?.block
  if (!b || !tractDominantType) return false
  return toTractVocabulary(b.rawType) !== tractDominantType
}
